import React, { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Box, useColorMode } from "@chakra-ui/react";
import {
  addToWatchlist,
  removeFromWatchlist,
} from "../../store/study/thunks";
import { selectWatchlist } from "../../store/study/selectors";
import useToastNotification from "../../hooks/useToastNotification";
import Watchlist from "../study/Watchlist";

// Note: the initial getWatchlist fetch is dispatched from Home.js's main
// mount effect — NOT from this component. Home wraps its content in an
// AnimatePresence keyed on ui.loading, so any thunk dispatched from inside
// the AccordionPanel subtree triggers a remount → re-fetch → re-mount loop.
// Keeping the fetch at the route level means it fires exactly once per
// /home visit.

const TICKER_RE = /^[A-Za-z0-9.\-]{1,10}$/;

const normalizeTicker = (raw) => {
  if (!raw) return null;
  const upper = String(raw).trim().toUpperCase();
  return TICKER_RE.test(upper) ? upper : null;
};

// Home dashboard card — renders the user's watchlist with add/remove and a
// per-row "Go to chart" affordance that deep-links into /study/<ticker>.
// Reuses the same <Watchlist> primitive used on the Study page; the only
// behavioral difference is that clicking a row navigates instead of selecting
// an active ticker on the same page.
export default function HomeWatchlistCard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { colorMode } = useColorMode();
  const showToast = useToastNotification();
  const watchlist = useSelector(selectWatchlist);
  // Local in-flight markers — the study mutation thunks are excluded from the
  // global ui.loading matcher (so Home doesn't swap to its skeleton on every
  // add/remove), so we surface the action's progress here instead.
  const [addingTicker, setAddingTicker] = useState(null);
  const [removingTicker, setRemovingTicker] = useState(null);

  const handleAdd = useCallback(
    async (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) {
        showToast("Invalid ticker symbol", { status: "error" });
        return;
      }
      setAddingTicker(ticker);
      try {
        const result = await dispatch(addToWatchlist({ ticker_name: ticker }));
        if (addToWatchlist.fulfilled.match(result)) {
          showToast(result.payload?.result || "Added to watchlist");
        }
      } finally {
        setAddingTicker(null);
      }
    },
    [dispatch, showToast]
  );

  const handleRemove = useCallback(
    async (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) return;
      setRemovingTicker(ticker);
      try {
        const result = await dispatch(removeFromWatchlist({ ticker_name: ticker }));
        if (removeFromWatchlist.fulfilled.match(result)) {
          showToast("Removed from watchlist");
        }
      } finally {
        setRemovingTicker(null);
      }
    },
    [dispatch, showToast]
  );

  const handleNavigate = useCallback(
    (raw) => {
      const ticker = normalizeTicker(raw);
      if (!ticker) return;
      navigate(`/study/${ticker}`);
    },
    [navigate]
  );

  return (
    <Box
      bg={colorMode === "light" ? "white" : "gray.700"}
      rounded="lg"
      p={4}
      boxShadow="sm"
    >
      <Watchlist
        heading={null}
        variant="card"
        watchlist={watchlist}
        selectedTicker={null}
        onSelectTicker={handleNavigate}
        onAddTicker={handleAdd}
        onRemoveTicker={handleRemove}
        onChartNavigate={handleNavigate}
        addingTicker={addingTicker}
        removingTicker={removingTicker}
      />
    </Box>
  );
}
